import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pill, Search, Check, Edit3, ArrowLeft } from "lucide-react";

/**
 * Big, touch-friendly catalog picker.
 * - Opens a dialog with categories first
 * - User taps category → list of medicines as chips
 * - Tap medicine → fills value + category and closes
 * - "No está en la lista" lets the user type a custom name
 */
export default function MedicineSelector({ value, category, onSelect, placeholder = "Tocar para elegir del catálogo" }) {
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [customMode, setCustomMode] = useState(false);
  const [customName, setCustomName] = useState("");

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data || [])).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    catalog.forEach((m) => {
      map[m.category] = map[m.category] || [];
      map[m.category].push(m);
    });
    return map;
  }, [catalog]);

  const filteredAll = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return catalog.filter((m) => {
      if (m.name.toLowerCase().includes(q)) return true;
      if (m.category.toLowerCase().includes(q)) return true;
      const aliases = m.aliases || [];
      return aliases.some((a) => a.toLowerCase().includes(q));
    });
  }, [catalog, search]);

  const resetState = () => {
    setSearch(""); setActiveCategory(null); setCustomMode(false); setCustomName("");
  };

  const handlePick = (m) => {
    onSelect({ medicine_name: m.name, category: m.category });
    setOpen(false);
    resetState();
  };

  const handleCustom = () => {
    if (!customName.trim()) return;
    onSelect({ medicine_name: customName.trim() });
    setOpen(false);
    resetState();
  };

  const categories = Object.keys(grouped).sort();

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetState(); }}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={`h-14 w-full mt-1.5 px-4 rounded-md border bg-slate-50 hover:bg-slate-100 text-left flex items-center gap-3 transition-colors ${value ? "border-blue-300" : "border-slate-200"}`}
          data-testid="medicine-selector-trigger"
        >
          <div className={`h-9 w-9 rounded-lg ${value ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"} grid place-items-center shrink-0`}>
            <Pill className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            {value ? (
              <>
                <div className="font-medium text-slate-900 truncate text-base">{value}</div>
                {category && <div className="text-xs text-slate-500">{category}</div>}
              </>
            ) : (
              <span className="text-slate-500 text-base">{placeholder}</span>
            )}
          </div>
          <Check className={`w-5 h-5 shrink-0 ${value ? "text-blue-600" : "text-slate-300"}`} />
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="medicine-selector-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="font-display text-blue-950 flex items-center gap-2">
            {activeCategory && !search && !customMode && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setActiveCategory(null)} data-testid="medicine-selector-back">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            {customMode ? "Escribir nombre del medicamento" : activeCategory || "Elige el medicamento"}
          </DialogTitle>
        </DialogHeader>

        {!customMode && (
          <div className="px-5 pt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar paracetamol, insulina…"
                className="h-12 pl-9 bg-slate-50 text-base"
                autoFocus
                data-testid="medicine-selector-search"
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5">
          {customMode ? (
            <div className="space-y-3" data-testid="medicine-selector-custom-mode">
              <Label className="text-sm font-medium text-slate-800">Nombre del medicamento</Label>
              <Input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Ej. Paracetamol pediátrico 100 mg"
                className="h-12 text-base bg-slate-50"
                autoFocus
                data-testid="medicine-selector-custom-input"
              />
              <p className="text-xs text-slate-500">Úsalo solo si no encuentras el medicamento en la lista. Recuerda incluir la dosis si la conoces.</p>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setCustomMode(false)}>Volver al catálogo</Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={!customName.trim()} onClick={handleCustom} data-testid="medicine-selector-custom-confirm">
                  Usar este nombre
                </Button>
              </div>
            </div>
          ) : search.trim() ? (
            // Search-driven flat list
            filteredAll.length === 0 ? (
              <div className="text-center py-8" data-testid="medicine-selector-no-results">
                <p className="text-slate-700 font-medium">Sin resultados para "{search}"</p>
                <Button variant="outline" className="mt-3" onClick={() => { setCustomMode(true); setCustomName(search); }}>
                  <Edit3 className="w-4 h-4 mr-2" /> Escribirlo a mano
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {filteredAll.map((m) => (
                  <MedicineChip key={m.name} m={m} onPick={handlePick} />
                ))}
              </div>
            )
          ) : activeCategory ? (
            // Single category list
            <div className="grid sm:grid-cols-2 gap-2">
              {(grouped[activeCategory] || []).map((m) => (
                <MedicineChip key={m.name} m={m} onPick={handlePick} />
              ))}
            </div>
          ) : (
            // Categories grid
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="medicine-selector-categories">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className="p-4 rounded-2xl border-2 border-blue-100 bg-blue-50/40 hover:bg-blue-50 transition-colors text-left btn-press"
                  data-testid={`medicine-selector-cat-${cat}`}
                >
                  <div className="font-display font-semibold text-blue-900">{cat}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{grouped[cat].length} opciones</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {!customMode && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
            <Button variant="ghost" className="w-full text-blue-700 hover:bg-blue-100" onClick={() => setCustomMode(true)} data-testid="medicine-selector-write-custom">
              <Edit3 className="w-4 h-4 mr-2" /> No está en la lista — escribirlo a mano
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MedicineChip({ m, onPick }) {
  const aliases = m.aliases || [];
  return (
    <button
      type="button"
      onClick={() => onPick(m)}
      className="text-left p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 transition-colors btn-press"
      data-testid={`medicine-selector-pick-${m.name}`}
    >
      <div className="font-medium text-sm text-slate-900">{m.name}</div>
      <div className="text-xs text-slate-500 mt-0.5">{m.category}</div>
      {aliases.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {aliases.slice(0, 3).map((a) => (
            <span key={a} className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-medium border border-slate-200">{a}</span>
          ))}
          {aliases.length > 3 && (
            <span className="text-[10px] text-slate-500">+{aliases.length - 3}</span>
          )}
        </div>
      )}
      <div className="mt-1.5 inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 border border-blue-100 text-blue-800 text-[11px] font-semibold">
        ${m.price_min}–${m.price_max} MXN
      </div>
    </button>
  );
}
